import React, { useEffect } from "react";
import { Provider } from "react-redux";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import rootEffect from "../effects/root-effect";
import { ConnectedRouter } from "connected-react-router";
import { BehaviorSubject } from "rxjs";
import { switchMap } from "rxjs/operators";
import configureStore, { history, runRootEpic, store } from "./configure-store";
import { InitialiseRequested } from "../actions";
import { ThemeProvider } from "@material-ui/styles";
import theme from "theme/index";
import CssBaseline from "@material-ui/core/CssBaseline";
import Routes from "./routes";
import SimpleSnackbarContainer from "features/notifications/components/simple-snackbar-container";
import { PersistGate } from "redux-persist/integration/react";
import { persistStore } from "redux-persist";
import DialogMessageContainer from "features/notifications/components/dialog-message-container";
import UpdateNotificationContainer from "features/notifications/components/update-notification-container";
import { Notifications } from "react-push-notification";

// Call configureStore to make sure any initialization logic runs
configureStore();

let persistor = persistStore(store);

const epic$ = new BehaviorSubject(rootEffect);

const hotReloadingEpic = (...args) =>
	epic$.pipe(switchMap((epic) => epic(...args)));

runRootEpic(hotReloadingEpic);

if (module.hot) {
	module.hot.accept("../effects/root-effect", () => {
		const nextRootEpic = require("../effects/root-effect").rootEffect;
		epic$.next(nextRootEpic);
	});
}

// Helper function to check if app was updating
const checkForUpdateCompletion = () => {
	const wasUpdating = localStorage.getItem('app_updating');
	if (wasUpdating === 'true') {
		console.log('App finished updating');
		// Clear the updating flag
		localStorage.removeItem('app_updating');
		localStorage.removeItem('app_update_time');
	}
};

// Helper function to check for service worker updates (production only)
const checkForServiceWorkerUpdates = () => {
	if (process.env.NODE_ENV !== "production" || !("serviceWorker" in navigator)) {
		return;
	}
	navigator.serviceWorker.getRegistrations().then((registrations) => {
		registrations.forEach((registration) => {
			registration.update().catch((err) => {
				console.warn("Service worker update check failed:", err);
			});
		});
	});
};

const App = () => {
	useEffect(() => {
		// Initialise once per page load (HMR remounts App but must not re-fetch org/profile).
		if (!window.__ftrFrameInitialised) {
			window.__ftrFrameInitialised = true;
			store.dispatch(InitialiseRequested());
		}
		
		// Check if the app just finished updating
		checkForUpdateCompletion();
		
		checkForServiceWorkerUpdates();

		let updateCheckInterval;
		if (process.env.NODE_ENV === "production") {
			updateCheckInterval = setInterval(checkForServiceWorkerUpdates, 30 * 60 * 1000);
		}

		return () => {
			if (updateCheckInterval) {
				clearInterval(updateCheckInterval);
			}
		};
		// eslint-disable-next-line
	}, []);

	return (
		<ThemeProvider theme={theme}>
			<MuiPickersUtilsProvider utils={MomentUtils}>
				<Provider store={store}>
					<PersistGate loading={null} persistor={persistor}>
						<ConnectedRouter history={history}>
							{process.env.NODE_ENV === 'production' && <Notifications />}
							<DialogMessageContainer />
							<Routes />
						</ConnectedRouter>
						<CssBaseline />
						<SimpleSnackbarContainer />
						<UpdateNotificationContainer />
					</PersistGate>
				</Provider>
			</MuiPickersUtilsProvider>
		</ThemeProvider>
	);
};

export default App;
